import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, untracked } from '@angular/core';

import { IconComponent } from '@/design-system/atoms/icon/icon.component';
import { NavItemComponent } from '@/design-system/molecules/nav-item/nav-item.component';
import { UserCardComponent } from '@/design-system/organisms/user-card/user-card.component';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { APP_URLS } from '@/routes/app.paths';
import type { NavItem } from '@/layout/sidebar/app.navigation';
import { NAV_ITEMS } from '@/layout/sidebar/app.navigation';
import { IconButtonComponent } from '@/design-system/molecules/icon-button/icon-button.component';
import { AuthStore } from '@/features/auth-store.service';
import { ProfilePhotoStore } from '@/features/profile-photo-store.service';

@Component({
  selector: 'app-sidebar',
  imports: [
    IconComponent,
    IconButtonComponent,
    NavItemComponent,
    UserCardComponent,
    RouterLink,
    RouterLinkActive,
    TranslocoPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sidebar.component.scss',
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent {
  private readonly authStore = inject(AuthStore);
  private readonly profilePhotoStore = inject(ProfilePhotoStore);
  private readonly router = inject(Router);
  protected readonly collapsed = signal(false);
  protected readonly user = this.authStore.currentUser;
  protected readonly photo = this.profilePhotoStore.photo;

  constructor() {
    effect(() => {
      if (this.user() === undefined || !this.authStore.hasPermission('GET_OWN_PROFILE_PHOTO')) return;
      untracked(() => void this.profilePhotoStore.loadOwnPhoto());
    });
  }

  protected toggleCollapse(): void {
    this.collapsed.update((v) => !v);
  }

  // TODO: replace with popover on usercard once the design is available
  protected logout(): void {
    this.authStore.logout();
    void this.router.navigateByUrl(APP_URLS.login);
  }

  protected readonly navItems = computed(() => NAV_ITEMS.filter((item) => this.isPermitted(item)));

  private isPermitted(item: NavItem): boolean {
    return item.permission === undefined || this.authStore.hasPermission(item.permission);
  }
}
